export interface EnvironmentInfo {
  label: string
  /** Production is deliberately NOT badged — see below. */
  showBadge: boolean
}

/**
 * Which environment the operator is looking at.
 *
 * This is a safety affordance, not decoration: Observatory can trigger and
 * inspect real ETL work, and not knowing whether you are on dev or production is
 * a genuine hazard.
 *
 * Production shows no badge on purpose. A badge that is always present stops
 * being read; absence-means-production keeps the non-production case loud.
 *
 * Derived from the host rather than configuration so it cannot drift out of sync
 * with where the app is actually deployed. `VITE_ENVIRONMENT` overrides it if a
 * build ever needs to be explicit.
 */
export function useEnvironment(): EnvironmentInfo {
  const override = import.meta.env.VITE_ENVIRONMENT as string | undefined
  if (override) {
    return { label: override, showBadge: !/^prod/i.test(override) }
  }

  if (import.meta.env.DEV) return { label: 'Local', showBadge: true }

  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  if (/(^|[-.])(dev|test|staging|stg)([-.]|$)/i.test(host)) {
    const matched = /(^|[-.])(dev|test|staging|stg)([-.]|$)/i.exec(host)?.[2] ?? 'Non-prod'
    return { label: matched.charAt(0).toUpperCase() + matched.slice(1).toLowerCase(), showBadge: true }
  }

  return { label: 'Production', showBadge: false }
}
